import { Box, HStack, IconButton } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  CalendarActions,
  type CalendarActionsProps,
} from "@src/components/ui-kit/datePicker/CalendarActions";
import { Icon } from "@src/components/ui-kit/Icon";
import { InputDate } from "@src/components/ui-kit/InputDatePicker/input-date";
import { useStore } from "@src/utils/hooks";
import {
  Calendar,
  CalendarControls,
  type CalendarDate,
  CalendarDays,
  CalendarMonth,
  CalendarMonthName,
  CalendarMonths,
  CalendarNextButton,
  CalendarPrevButton,
  type CalendarValues,
  CalendarWeek,
} from "@uselessdev/datepicker";
import { isSameDay } from "date-fns";
import { type ReactNode, useEffect, useState } from "react";
import { formatDate, getDateFromEUFormat } from ".";

export type MultiMonthCalendarProps = Calendar & {
  hideCalendarActions?: boolean;
  topExtraContent?: ReactNode;
  months?: number;
} & Pick<CalendarActionsProps, "customActions">;

export const MultiMonthCalendarControls = () => {
  return (
    <CalendarControls>
      <CalendarPrevButton
        as={(props) => (
          <IconButton
            h="7"
            aria-label={t`Prev month`}
            colorScheme="grey"
            icon={<Icon name="arrow-narrow-left" w="5" h="5" />}
            variant="subtle"
            {...props}
          />
        )}
      />
      <CalendarNextButton
        as={(props) => (
          <IconButton
            h="7"
            aria-label={t`Next month`}
            colorScheme="grey"
            icon={<Icon name="arrow-narrow-right" w="5" h="5" />}
            variant="subtle"
            {...props}
          />
        )}
      />
    </CalendarControls>
  );
};

export const MultiMonthCalendar = ({
  hideCalendarActions,
  customActions,
  topExtraContent,
  months = 2,
  singleDateSelection,
  ...props
}: MultiMonthCalendarProps) => {
  const { workspaceStore } = useStore();

  const value = props.value as { start?: Date; end?: Date } | undefined;

  const [startDateInput, setStartDateInput] = useState(
    formatDate(value?.start),
  );
  const [endDateInput, setEndDateInput] = useState(formatDate(value?.end));
  const [isTyping, setIsTyping] = useState(false);
  const [rangeAnchor, setRangeAnchor] = useState<CalendarDate | null>(null);

  useEffect(() => {
    if (isTyping) return;
    setStartDateInput(formatDate(value?.start));
    setEndDateInput(formatDate(value?.end));
  }, [value?.start, value?.end, isTyping]);

  const handleDateChange = (value: CalendarValues | CalendarDate) => {
    if (typeof value === "number" || value instanceof Date) {
      setRangeAnchor(null);
      props.onSelectDate(value);
      return;
    }

    const rangeIsDefinedInValue = value.start && value.end;
    const rangeIsDefinedInProps = props.value.start && props.value.end;

    if (rangeIsDefinedInValue && rangeIsDefinedInProps) {
      const startChanged = !isSameDay(props.value.start!, value.start!);
      const endChanged = !isSameDay(props.value.end!, value.end!);

      if (endChanged && !startChanged) {
        if (rangeAnchor) {
          props.onSelectDate({ start: rangeAnchor, end: value.end! });
          setRangeAnchor(null);
        } else {
          setRangeAnchor(value.end!);
          props.onSelectDate(value);
        }
        return;
      }

      if (startChanged && !endChanged) {
        if (rangeAnchor) {
          props.onSelectDate({ start: value.start!, end: rangeAnchor });
          setRangeAnchor(null);
        } else {
          setRangeAnchor(value.start!);
          props.onSelectDate(value);
        }
        return;
      }

      setRangeAnchor(null);
    }

    props.onSelectDate(value);
  };

  const handleStartDateChange = (val: string) => {
    setIsTyping(true);
    setStartDateInput(val);
    const parsedDate = getDateFromEUFormat(val);

    if (!parsedDate) return;

    const year = parsedDate.getFullYear();

    if (year > 2100 || year < 1983) return;

    if (singleDateSelection) {
      props.onSelectDate(parsedDate);
    } else {
      props.onSelectDate({
        start: parsedDate,
        end: value?.end,
      });
    }
  };

  const handleEndDateChange = (val: string) => {
    setIsTyping(true);
    setEndDateInput(val);
    const parsedDate = getDateFromEUFormat(val);

    if (!parsedDate) return;

    const year = parsedDate.getFullYear();

    if (year > 2100 || year < 1983) return;

    props.onSelectDate({
      start: value?.start,
      end: parsedDate,
    });
  };

  const handleInputBlur = () => {
    setIsTyping(false);
  };

  return (
    <Calendar
      months={months}
      weekStartsOn={workspaceStore.settings?.startOfWeekNumber}
      highlightToday
      allowSelectSameDay
      singleDateSelection={singleDateSelection}
      {...props}
      onSelectDate={handleDateChange}
    >
      {!hideCalendarActions && (
        <CalendarActions
          setDates={props.onSelectDate}
          customActions={customActions}
        />
      )}
      <Box pos="relative">
        {topExtraContent}
        <HStack px="4" pt="4" spacing="2">
          <InputDate
            placeholder={t`Start date`}
            value={startDateInput}
            onChange={handleStartDateChange}
            onBlur={handleInputBlur}
          />
          {!singleDateSelection && (
            <InputDate
              placeholder={t`End date`}
              value={endDateInput}
              onChange={handleEndDateChange}
              onBlur={handleInputBlur}
            />
          )}
        </HStack>
        <MultiMonthCalendarControls />

        <CalendarMonths
          display="flex"
          flexDir={{
            base: "column",
            md: "row",
          }}
        >
          {[...Array(months).keys()].map((month) => (
            <CalendarMonth month={month} key={month}>
              <CalendarMonthName />
              <CalendarWeek />
              <CalendarDays />
            </CalendarMonth>
          ))}
        </CalendarMonths>
      </Box>
    </Calendar>
  );
};
